import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Button, 
  OutlineButton, 
  Badge, 
  FlexContainer,
  Grid
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { User, SystemLog, UserRole } from '../../types';
import apiService from '../../services/api';
import CustomDialog from '../common/CustomDialog';

const BackButton = styled(OutlineButton)`
  margin-bottom: 1rem;
`;

const DetailItem = styled.div`
  margin-bottom: 1rem;
  
  h4 {
    margin: 0 0 0.5rem 0;
    color: var(--secondary-color);
    font-size: 1rem;
  }
  
  p {
    margin: 0;
    color: var(--text-color);
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  span {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const ActivityTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  
  th, td {
    padding: 0.75rem;
    border-bottom: 1px solid var(--light-gray);
    text-align: left;
  }
  
  th {
    background-color: var(--light-gray);
    color: var(--secondary-color);
    font-weight: 600;
  }
  
  tr:nth-child(even) {
    background-color: rgba(0, 0, 0, 0.02);
  }
`;

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [activityLogs, setActivityLogs] = useState<SystemLog[]>([]);
  const [regenerating, setRegenerating] = useState(false);
  const [newActivationLink, setNewActivationLink] = useState<string | null>(null);
  const [showActivationLink, setShowActivationLink] = useState(false);
  
  // Dialog state
  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });
  
  // Check if current user has admin privileges
  const canManageUsers = currentUser?.role === 'MD' || currentUser?.role === 'TD';

  const showDialog = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setDialog({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    const loadUserData = async () => {
      if (id && id !== 'undefined' && id !== 'null') {
        try {
          setLoading(true);
          
          // Load user data
          const userResponse = await apiService.getUser(id);
          if (userResponse.success && userResponse.data) {
            setUser(userResponse.data);
          } else {
            throw new Error('Failed to load user data');
          }
          
          // Load activity logs (not implemented yet)
          const mockLogs: SystemLog[] = [
            {
              id: '1',
              userId: id,
              userName: userResponse.data?.name || '',
              userRole: (userResponse.data?.role || 'MD') as UserRole,
              action: 'Login',
              module: 'Authentication',
              resourceId: id,
              resourceType: 'User',
              details: { ipAddress: '192.168.1.1' },
              ipAddress: '192.168.1.1',
              userAgent: 'Mozilla/5.0...',
              timestamp: new Date(),
              success: true
            },
            {
              id: '2',
              userId: id,
              userName: userResponse.data?.name || '',
              userRole: (userResponse.data?.role || 'MD') as UserRole,
              action: 'Update Profile',
              module: 'User Management',
              resourceId: id,
              resourceType: 'User',
              details: { field: 'email' },
              ipAddress: '192.168.1.1',
              userAgent: 'Mozilla/5.0...',
              timestamp: new Date(Date.now() - 86400000), // 1 day ago
              success: true
            }
          ];
          setActivityLogs(mockLogs);
        } catch (error: any) {
          console.error('Error loading user data:', error);
          // Handle error appropriately
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
        // Handle invalid ID
      }
    };

    loadUserData();
  }, [id]);

  if (loading) {
    return <div>Loading user details...</div>;
  }

  if (!user) {
    return <div>User not found</div>;
  }

  const formatDate = (date: Date | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusVariant = (status: string): 'success' | 'danger' | 'warning' => {
    switch (status) {
      case 'Active': return 'success';
      case 'Inactive': return 'warning';
      case 'Suspended': return 'danger';
      default: return 'warning';
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!user) return;
    
    const userId = user._id || user.id;
    if (!userId) {
      showDialog('Error', 'Invalid user ID', 'error');
      return;
    }
    
    try {
      const response = await apiService.updateUserStatus(userId, newStatus);
      if (response.success) {
        showDialog(
          'Success', 
          `User status updated to ${newStatus} successfully.`, 
          'success'
        );
        // Reload user data to get updated status
        window.location.reload();
      } else {
        showDialog('Error', 'Failed to update user status. Please try again.', 'error');
      }
    } catch (error: any) {
      console.error('Error updating user status:', error);
      const errorMessage = error.message || 'Failed to update user status. Please try again.';
      showDialog('Error', errorMessage, 'error');
    }
  };

  const confirmStatusChange = (newStatus: string) => {
    if (!user) return;
    
    const action = newStatus === 'Active' ? 'activate' : newStatus.toLowerCase();
    const message = `Are you sure you want to ${action} user "${user.name}"? This will ${newStatus === 'Active' ? 'allow' : 'prevent'} them from signing in to the system.`;
    
    if (window.confirm(message)) {
      handleStatusChange(newStatus);
    }
  };



  const regenerateActivationLink = async () => {
    if (!user || !canManageUsers) return;
    
    try {
      setRegenerating(true);
      const response = await apiService.regenerateActivationToken(user._id || user.id || '');
      
      if (response.success) {
        setNewActivationLink(response.data.activationLink);
        setShowActivationLink(true);
        showDialog('Success', 'New activation link generated successfully!', 'success');
      } else {
        showDialog('Error', 'Failed to generate new activation link', 'error');
      }
    } catch (error: any) {
      console.error('Error regenerating activation link:', error);
      showDialog('Error', error.message || 'Failed to generate new activation link', 'error');
    } finally {
      setRegenerating(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        showDialog('Success', 'Activation link copied to clipboard!', 'success');
      } else {
        // Fallback for older browsers or non-HTTPS
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          showDialog('Success', 'Activation link copied to clipboard!', 'success');
        } catch (err) {
          console.error('Fallback copy failed:', err);
          // Show manual copy instructions
          showDialog('Copy Instructions', 
            'Please manually copy this link: ' + text, 
            'info');
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (err) {
      console.error('Failed to copy: ', err);
      // Show manual copy instructions
      showDialog('Copy Instructions', 
        'Please manually copy this link: ' + text, 
        'info');
    }
  };

  return (
    <div>
      <BackButton as={Link} to="/users">
        &larr; Back to Users List
      </BackButton>
      
      <FlexContainer justify="space-between" style={{ marginBottom: '1rem' }}>
        <h1 className="page-title">User Profile</h1>
        {canManageUsers && (
          <FlexContainer gap="0.5rem">
            <OutlineButton as={Link} to={`/users/edit/${user.id}`}>
              Edit User
            </OutlineButton>
            {user.status === 'Active' ? (
              <Button 
                onClick={() => confirmStatusChange('Suspended')}
                style={{ backgroundColor: 'var(--danger-color)' }}
              >
                Suspend User
              </Button>
            ) : (
              <Button 
                onClick={() => confirmStatusChange('Active')}
                style={{ backgroundColor: 'var(--success-color)' }}
              >
                Activate User
              </Button>
            )}
          </FlexContainer>
        )}
      </FlexContainer>
      
      <Grid columns="1fr 2fr" gap="1.5rem">
        <Card>
          <CardHeader>
            <h3>User Information</h3>
          </CardHeader>
          <CardBody>
            <FlexContainer direction="column" align="flex-start" gap="1.5rem">
              <DetailItem>
                <h4>Name</h4>
                <p>{user.name}</p>
              </DetailItem>
              
              <DetailItem>
                <h4>Email</h4>
                <p>{user.email}</p>
              </DetailItem>
              
              <DetailItem>
                <h4>Role</h4>
                <p>{user.role}</p>
              </DetailItem>
              
              <DetailItem>
                <h4>Status</h4>
                <Badge variant={getStatusVariant(user.status)}>
                  {user.status}
                </Badge>
              </DetailItem>
              
              <DetailItem>
                <h4>Last Login</h4>
                <p>{formatDate(user.lastLogin)}</p>
              </DetailItem>
              
              <DetailItem>
                <h4>Created</h4>
                <p>{formatDate(user.createdAt)}</p>
              </DetailItem>
              

              
              {canManageUsers && (
                <Button 
                  onClick={regenerateActivationLink}
                  disabled={regenerating}
                  style={{ backgroundColor: 'var(--warning-color)' }}
                >
                  {regenerating ? 'Generating...' : 'Regenerate Activation Link'}
                </Button>
              )}
              
              {showActivationLink && newActivationLink && (
                <DetailItem>
                  <h4>New Activation Link</h4>
                  <div style={{ 
                    background: 'var(--light-gray)', 
                    padding: '0.5rem', 
                    borderRadius: 'var(--border-radius)',
                    wordBreak: 'break-all',
                    fontSize: '0.8rem'
                  }}>
                    {newActivationLink}
                  </div>
                  <Button 
                    onClick={() => copyToClipboard(newActivationLink)}
                    style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}
                  >
                    Copy Link
                  </Button>
                </DetailItem>
              )}
            </FlexContainer>
          </CardBody>
        </Card>
        
        <div>
          <Card>
            <CardHeader>
              <h3>Contact Details</h3>
            </CardHeader>
            <CardBody>
              <ContactInfo>
                {user.contactDetails?.phone && (
                  <span>
                    <strong>Phone:</strong> {user.contactDetails.phone}
                  </span>
                )}
                
                {user.contactDetails?.address && (
                  <span>
                    <strong>Address:</strong> {user.contactDetails.address}
                  </span>
                )}
                
                {user.contactDetails?.department && (
                  <span>
                    <strong>Department:</strong> {user.contactDetails.department}
                  </span>
                )}
              </ContactInfo>
            </CardBody>
          </Card>
          
          <Card style={{ marginTop: '1.5rem' }}>
            <CardHeader>
              <h3>Recent Activity</h3>
            </CardHeader>
            <CardBody>
              {activityLogs.length > 0 ? (
                <ActivityTable>
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>Module</th>
                      <th>Time</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityLogs.map(log => (
                      <tr key={log.id}>
                        <td>{log.action}</td>
                        <td>{log.module}</td>
                        <td>{formatDate(log.timestamp)}</td>
                        <td>
                          <Badge variant={log.success ? 'success' : 'danger'}>
                            {log.success ? 'Success' : 'Failed'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </ActivityTable>
              ) : (
                <p>No recent activity found.</p>
              )}
            </CardBody>
          </Card>
        </div>
      </Grid>

      {/* Custom Dialog */}
      <CustomDialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
        confirmText="OK"
        showCancel={false}
      />
    </div>
  );
};

export default UserDetail; 